import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useCloseClient, type ClientRow } from '@/hooks/data/useClients';

// "Ugasi prodavca" (batch BE) — closes a seller who sold elsewhere or
// withdrew from selling. The reason is MANDATORY (min 5 chars) and is
// stored permanently with WHO closed and WHEN (columns + Verlauf history).
// The responsible makler gets a notification when someone else closes.
export function CloseSellerDialog({
  client,
  open,
  onOpenChange,
}: {
  client: ClientRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const close = useCloseClient();
  const [reason, setReason] = useState('');
  const valid = reason.trim().length >= 5;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('clients.close.title', 'Verkäufer schließen')}</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-ink-2">
          {t(
            'clients.close.body',
            'Z. B. „Wohnung anderweitig verkauft" oder „Verkauf zurückgezogen". Der Grund ist Pflicht und wird dauerhaft gespeichert (wer, wann, warum). Der zuständige Makler wird benachrichtigt.',
          )}
        </p>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder={t('clients.close.reason_ph', 'Grund (Pflichtfeld, min. 5 Zeichen)')}
          className="w-full rounded-md border border-line bg-paper px-3 py-2 text-sm text-ink placeholder:text-ink-3 focus:outline-none focus:ring-2 focus:ring-red-400/40"
        />
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md border border-line px-3 py-1.5 text-sm text-ink hover:bg-paper-2"
          >
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button
            type="button"
            disabled={!valid || close.isPending}
            onClick={() =>
              close.mutate(
                { id: client.id, reason: reason.trim() },
                {
                  onSuccess: () => {
                    onOpenChange(false);
                    toast.success(
                      t('clients.close.done', 'Verkäufer geschlossen — Grund im Verlauf gespeichert.'),
                    );
                  },
                  onError: (e) => toast.error((e as Error).message),
                },
              )
            }
            className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {close.isPending
              ? t('clients.close.closing', 'Wird geschlossen …')
              : t('clients.close.confirm', 'Schließen')}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
